import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Modal, Form, Badge } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaTimes } from 'react-icons/fa';
import { campaignsApi, adSetsApi, competitionsApi, feedAdsApi } from '../../../services/sponsoredAdsApi';
import SetAlert from '../../../components/SetAlert';
import { useNavigate } from 'react-router-dom';
import {
  FEED_PLACEMENT_KEYS,
  FEED_PLACEMENT_LABELS,
  FEED_PLACEMENT_MAX_ADS,
  formatPlacementList
} from '../feedAdPlacements';

const Campaigns = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [adSets, setAdSets] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [alertMessage, setAlertMessage] = useState({ success: '', error: '' });

  const [placementOptions, setPlacementOptions] = useState(FEED_PLACEMENT_KEYS);

  const [formData, setFormData] = useState({
    campaignName: '',
    competitionId: '',
    startTime: '',
    endTime: '',
    frequencyPercentage: 10,
    status: 'active',
    placements: ['explore_feed'],
    adSets: []
  });

  useEffect(() => {
    fetchCampaigns();
    fetchAdSets();
    fetchCompetitions();
    fetchPlacementOptions();
  }, []);

  const fetchPlacementOptions = async () => {
    try {
      const response = await feedAdsApi.getPlacements();
      if (response.statusCode === '10000' && Array.isArray(response.data?.placements)) {
        setPlacementOptions(response.data.placements);
      }
    } catch (error) {
      console.error('Error fetching feed placements:', error);
    }
  };

  const mapAdSetsFromApi = (adSets) =>
    (adSets || []).map((adSet) => ({
      adSetId: adSet.ad_set_id ?? adSet.adSetId ?? '',
      priority: adSet.priority ?? 1,
      contributionPercentage: adSet.contribution_percentage ?? adSet.contributionPercentage ?? 0,
      maxImpressions: adSet.max_impressions ?? adSet.maxImpressions ?? 0
    }));

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await campaignsApi.getAll();
      if (response.statusCode === "10000") {
        const transformedData = Array.isArray(response.data) 
          ? response.data.map(campaign => ({
              id: campaign.id,
              campaignName: campaign.campaign_name,
              competitionId: campaign.competition_id,
              startTime: campaign.start_time,
              endTime: campaign.end_time,
              frequencyPercentage: campaign.frequency_percentage,
              status: campaign.status || 'active',
              placements: campaign.placements || [],
              adSets: mapAdSetsFromApi(campaign.ad_sets),
              createdAt: campaign.created_at,
              updatedAt: campaign.updated_at
            }))
          : [];
        setCampaigns(transformedData);
      } else {
        setAlertMessage(prev => ({ ...prev, error: response.message || 'Failed to fetch campaigns' }));
        setCampaigns([]);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setAlertMessage(prev => ({ ...prev, error: 'Error fetching campaigns' }));
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdSets = async () => {
    try {
      const response = await adSetsApi.getAll();
      if (response.statusCode === "10000") {
        const transformedData = Array.isArray(response.data) 
          ? response.data.map(adSet => ({
              id: adSet.id,
              adSetName: adSet.ad_set_name,
              brandName: adSet.brand_name,
              status: adSet.status || 'active'
            }))
          : [];
        setAdSets(transformedData);
      } else {
        setAlertMessage(prev => ({ ...prev, error: response.message || 'Failed to fetch ad sets' }));
        setAdSets([]);
      }
    } catch (error) {
      console.error('Error fetching ad sets:', error);
      setAlertMessage(prev => ({ ...prev, error: 'Error fetching ad sets' }));
      setAdSets([]);
    }
  };

  const fetchCompetitions = async () => {
    try {
      const response = await competitionsApi.getAll();
      console.log('Competitions response:', response);
      if (response.statusCode === "10000") {
        const transformedData = Array.isArray(response.data.competitions) 
          ? response.data.competitions.map(competition => ({
              id: competition.cid,
              name: competition.title,
              status: competition.status
            }))
          : [];
        console.log('Transformed competitions:', transformedData);
        setCompetitions(transformedData);
      } else {
        setAlertMessage(prev => ({ ...prev, error: response.message || 'Failed to fetch competitions' }));
        setCompetitions([]);
      }
    } catch (error) {
      console.error('Error fetching competitions:', error);
      setAlertMessage(prev => ({ ...prev, error: 'Error fetching competitions' }));
      setCompetitions([]);
    }
  };

  const emptyFormData = () => ({
    campaignName: '',
    competitionId: '',
    startTime: '',
    endTime: '',
    frequencyPercentage: 10,
    status: 'active',
    placements: ['explore_feed'],
    adSets: []
  });

  const handleShowModal = (campaign = null) => {
    if (campaign) {
      setFormData({
        campaignName: campaign.campaignName,
        competitionId: campaign.competitionId,
        startTime: campaign.startTime ? new Date(campaign.startTime).toISOString().slice(0, 16) : '',
        endTime: campaign.endTime ? new Date(campaign.endTime).toISOString().slice(0, 16) : '',
        frequencyPercentage: campaign.frequencyPercentage,
        status: campaign.status,
        placements: campaign.placements?.length ? campaign.placements : ['explore_feed'],
        adSets: mapAdSetsFromApi(campaign.adSets)
      });
      setSelectedCampaign(campaign);
    } else {
      setFormData(emptyFormData());
      setSelectedCampaign(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCampaign(null);
    setFormData(emptyFormData());
  };

  const handlePlacementToggle = (key) => {
    setFormData((prev) => {
      const current = prev.placements || [];
      const next = current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key];
      return { ...prev, placements: next };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddAdSet = () => {
    setFormData(prev => ({
      ...prev,
      adSets: [
        ...prev.adSets,
        {
          adSetId: '',
          priority: prev.adSets.length + 1,
          contributionPercentage: 0,
          maxImpressions: 0
        }
      ]
    }));
  };

  const handleRemoveAdSet = (index) => {
    setFormData(prev => ({
      ...prev,
      adSets: prev.adSets.filter((_, i) => i !== index)
    }));
  };

  const handleAdSetChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      adSets: prev.adSets.map((adSet, i) => {
        if (i === index) {
          return { ...adSet, [field]: value };
        }
        return adSet;
      })
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.placements?.length) {
      setAlertMessage((prev) => ({
        ...prev,
        error: 'Select at least one feed placement.',
        success: ''
      }));
      return;
    }

    if (formData.adSets.length > 0) {
      const totalContribution = formData.adSets.reduce(
        (sum, adSet) => sum + parseInt(adSet.contributionPercentage, 10),
        0
      );
      if (totalContribution !== 100) {
        setAlertMessage((prev) => ({
          ...prev,
          error: `Ad set contribution percentages must sum to 100 (currently ${totalContribution}).`,
          success: ''
        }));
        return;
      }
    }

    try {
      const apiData = {
        campaign_name: formData.campaignName,
        competition_id: parseInt(formData.competitionId, 10),
        start_time: new Date(formData.startTime).toISOString(),
        end_time: new Date(formData.endTime).toISOString(),
        frequency_percentage: parseInt(formData.frequencyPercentage, 10),
        status: formData.status,
        placements: formData.placements
      };

      const adSetsPayload = formData.adSets.map((adSet) => ({
        ad_set_id: parseInt(adSet.adSetId, 10),
        priority: parseInt(adSet.priority, 10),
        contribution_percentage: parseInt(adSet.contributionPercentage, 10),
        max_impressions: parseInt(adSet.maxImpressions, 10)
      }));

      let response;
      if (selectedCampaign) {
        if (adSetsPayload.length > 0) {
          apiData.ad_sets = adSetsPayload;
        }
        response = await campaignsApi.update(selectedCampaign.id, apiData);
      } else if (adSetsPayload.length > 0) {
        response = await campaignsApi.createWithAdSets({
          ...apiData,
          ad_sets: adSetsPayload
        });
      } else {
        response = await campaignsApi.create(apiData);
      }

      if (response?.success || response?.statusCode === '10000') {
        handleCloseModal();
        setAlertMessage((prev) => ({
          ...prev,
          success: `Campaign ${selectedCampaign ? 'updated' : 'created'} successfully`,
          error: ''
        }));
        await fetchCampaigns();
        navigate('/sponsored-ads/campaigns', { replace: true });
      } else {
        setAlertMessage((prev) => ({
          ...prev,
          error: response?.message || response?.error || `Failed to ${selectedCampaign ? 'update' : 'create'} campaign`,
          success: ''
        }));
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      setAlertMessage((prev) => ({
        ...prev,
        error: `Error ${selectedCampaign ? 'updating' : 'creating'} campaign`,
        success: ''
      }));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      try {
        const response = await campaignsApi.delete(id);
        if (response.statusCode === "10000") {
          setAlertMessage(prev => ({ 
            ...prev, 
            success: response.message || 'Campaign deleted successfully' 
          }));
          fetchCampaigns();
        } else {
          setAlertMessage(prev => ({ 
            ...prev, 
            error: response.message || 'Failed to delete campaign' 
          }));
        }
      } catch (error) {
        console.error('Error deleting campaign:', error);
        setAlertMessage(prev => ({ 
          ...prev, 
          error: 'Error deleting campaign' 
        }));
      }
    }
  };

  const filteredCampaigns = Array.isArray(campaigns) ? campaigns.filter(campaign => {
    const matchesSearch = campaign.campaignName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  }) : [];

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="campaigns-container">
      <SetAlert alertMessage={alertMessage} setAlertMessage={setAlertMessage} />
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Campaigns</h4>
          <Button variant="primary" onClick={() => handleShowModal()}>
            <FaPlus className="me-2" />
            New Campaign
          </Button>
        </Card.Header>
        <Card.Body>
          <Row className="mb-4">
            <Col md={12}>
              <Form.Control
                type="text"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
          </Row>

          <Table responsive hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Competition ID</th>
                <th>Placements</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Frequency %</th>
                <th>Ad Sets</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCampaigns.map(campaign => (
                <tr key={campaign.id}>
                  <td>{campaign.campaignName}</td>
                  <td>{campaign.competitionId}</td>
                  <td>
                    <small>{formatPlacementList(campaign.placements)}</small>
                  </td>
                  <td>{new Date(campaign.startTime).toLocaleDateString()}</td>
                  <td>{new Date(campaign.endTime).toLocaleDateString()}</td>
                  <td>{campaign.frequencyPercentage}%</td>
                  <td>{campaign.adSets?.length || 0}</td>
                  <td>
                    <Badge bg={campaign.status === 'active' ? 'success' : 'secondary'}>
                      {campaign.status}
                    </Badge>
                  </td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleShowModal(campaign)}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(campaign.id)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedCampaign ? 'Edit Campaign' : 'New Campaign'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Campaign Name</Form.Label>
              <Form.Control
                type="text"
                name="campaignName"
                value={formData.campaignName}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Competition</Form.Label>
              <Form.Select
                name="competitionId"
                value={formData.competitionId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Competition</option>
                {Array.isArray(competitions) && competitions.map(competition => (
                  <option key={competition.id} value={competition.id}>
                    {competition.name} (CID: {competition.id})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Time</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Time</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Frequency Percentage</Form.Label>
              <Form.Control
                type="number"
                name="frequencyPercentage"
                value={formData.frequencyPercentage}
                onChange={handleInputChange}
                min="0"
                max="100"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Feed Placements</Form.Label>
              <Form.Text className="d-block mb-2 text-muted">
                Surfaces where this campaign can serve feed ads.
              </Form.Text>
              <div className="d-flex flex-column gap-2">
                {placementOptions.map((key) => (
                  <Form.Check
                    key={key}
                    type="checkbox"
                    id={`campaign-placement-${key}`}
                    label={
                      <span>
                        {FEED_PLACEMENT_LABELS[key] || key}
                        <small className="text-muted ms-2">
                          (max {FEED_PLACEMENT_MAX_ADS[key] ?? '?'} ads/page)
                        </small>
                      </span>
                    }
                    checked={(formData.placements || []).includes(key)}
                    onChange={() => handlePlacementToggle(key)}
                  />
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                required
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="paused">Paused</option>
              </Form.Select>
            </Form.Group>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Ad Sets</h5>
                <Button variant="success" size="sm" onClick={handleAddAdSet}>
                  <FaPlus className="me-1" /> Add Ad Set
                </Button>
              </div>
              {formData.adSets.map((adSet, index) => (
                <Card key={index} className="mb-2">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="mb-0">Ad Set {index + 1}</h6>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveAdSet(index)}
                      >
                        <FaTimes />
                      </Button>
                    </div>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>Ad Set</Form.Label>
                          <Form.Select
                            value={adSet.adSetId}
                            onChange={(e) => handleAdSetChange(index, 'adSetId', e.target.value)}
                            required
                          >
                            <option value="">Select Ad Set</option>
                            {Array.isArray(adSets) && adSets.map(as => (
                              <option key={as.id} value={as.id}>
                                {as.adSetName}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>Priority</Form.Label>
                          <Form.Control
                            type="number"
                            value={adSet.priority}
                            onChange={(e) => handleAdSetChange(index, 'priority', e.target.value)}
                            min="1"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>Contribution %</Form.Label>
                          <Form.Control
                            type="number"
                            value={adSet.contributionPercentage}
                            onChange={(e) => handleAdSetChange(index, 'contributionPercentage', e.target.value)}
                            min="0"
                            max="100"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>Max Impressions</Form.Label>
                          <Form.Control
                            type="number"
                            value={adSet.maxImpressions}
                            onChange={(e) => handleAdSetChange(index, 'maxImpressions', e.target.value)}
                            min="0"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedCampaign ? 'Update' : 'Create'} Campaign
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Campaigns; 